import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { createUpsellOffers, nestedUpsellItems } from '@theme/yard-sign-upsell-helpers';

/** One offer, presented in either the configured cart drawer or a native modal. */
class YardSignUpsell extends Component {
  requiredRefs = ['offerTemplate', 'popup', 'status'];
  #controller;
  #sourceSection;
  #offers = [];
  #pendingOffers;
  #card;
  #row;
  #observer;
  #returnFocus;
  #busy = false;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#sourceSection = this.closest('.shopify-section');
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.refs.popup.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    }, { signal });
    this.refs.popup.addEventListener('click', (event) => {
      if (event.target !== this.refs.popup) return;
      const rect = this.refs.popup.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dismiss();
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#clearOffer();
  }

  #onCartUpdate = async (event) => {
    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const isSignAdd = event.action === 'add' && this.#sourceSection?.contains(form) && form?.dataset.productId === this.dataset.productId;
    const generation = isSignAdd ? ++this.#generation : this.#generation;
    try {
      const result = await event.promise;
      const detail = result?.detail;
      if (generation !== this.#generation || detail?.didError || !detail?.items) return;
      if (!isSignAdd) {
        if (!this.#busy && this.#offers.some((offer) => !detail.items.some((item) => item.key === offer.parentKey))) this.dismiss();
        return;
      }
      const offers = createUpsellOffers(detail.items, detail.addedLines);
      const quantity = offers.reduce((sum, offer) => sum + offer.quantity, 0);
      const alreadyInCart = detail.items.filter((item) => String(item.variant_id) === this.dataset.variantId).reduce((sum, item) => sum + item.quantity, 0);
      const min = Number(this.dataset.min);
      const step = Number(this.dataset.step);
      if (!quantity || offers.some((offer) => offer.quantity < min || offer.quantity % step !== 0 || (this.dataset.max && offer.quantity > Number(this.dataset.max))) || (this.dataset.stock && alreadyInCart + quantity > Number(this.dataset.stock))) return;
      if (this.#busy) {
        this.#pendingOffers = offers;
      } else {
        await this.#present(offers);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[yard-sign-upsell] Could not prepare the offer', error);
    }
  };

  async #present(offers) {
    this.#clearOffer();
    this.#offers = offers;
    this.#returnFocus = document.activeElement;
    this.#card = this.refs.offerTemplate.content.firstElementChild.cloneNode(true);
    const quantity = offers.reduce((sum, offer) => sum + offer.quantity, 0);
    for (const label of this.#card.querySelectorAll('[data-upsell-quantity]')) label.textContent = String(quantity);
    this.#card.querySelector('[data-upsell-total]').innerHTML = formatMoney(quantity * Number(this.dataset.price), this.dataset.moneyFormat, this.dataset.currency);
    this.#card.querySelector('[data-upsell-add]').addEventListener('click', this.#accept, { signal: this.#controller.signal });
    this.#card.querySelector('[data-upsell-decline]').addEventListener('click', () => this.dismiss(), { signal: this.#controller.signal });
    if (this.dataset.presentation === 'drawer') {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (!drawer) return;
      await import('@theme/theme-drawer');
      this.#observer = new MutationObserver(() => this.#mountDrawer(drawer));
      this.#observer.observe(drawer, { childList: true, subtree: true });
      this.#mountDrawer(drawer);
      drawer.open();
    } else {
      this.refs.popup.append(this.#card);
      lockScroll(this.refs.popup);
      this.refs.popup.showModal();
      this.#card.querySelector('[data-upsell-add]').focus();
    }
  }

  #mountDrawer(drawer) {
    if (!this.#offers.length || this.#row?.isConnected) return;
    const parentKey = this.#offers.at(-1).parentKey;
    const parentRow = Array.from(drawer.querySelectorAll('tr[data-key]')).find((row) => row.dataset.key === parentKey);
    if (!parentRow) return;
    this.#row = document.createElement('tr');
    this.#row.className = 'yard-sign-upsell__row';
    const cell = document.createElement('td');
    cell.colSpan = 5;
    cell.append(this.#card);
    this.#row.append(cell);
    parentRow.after(this.#row);
  }

  #accept = async () => {
    if (this.#busy || !this.#offers.length) return;
    this.#busy = true;
    const card = this.#card;
    const addButton = card.querySelector('[data-upsell-add]');
    const declineButton = card.querySelector('[data-upsell-decline]');
    const errorLabel = card.querySelector('[data-upsell-error]');
    addButton.disabled = declineButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    errorLabel.hidden = true;
    let mutationStarted = false;
    let accepted = false;
    let deferred;
    let cartBefore;
    let addedSections;
    try {
      const cart = await this.#fetchCart();
      cartBefore = cart;
      const items = nestedUpsellItems(this.#offers, cart.items, this.dataset.variantId);
      const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (element) => element.dataset.sectionId);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
      mutationStarted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: Array.from(new Set(sections)), sections_url: location.pathname }),
      });
      const data = await response.json();
      addedSections = data.sections;
      accepted = response.ok && !data.status;
      const updatedCart = await this.#fetchCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart), detail: { items: updatedCart.items, sections: data.sections, source: 'yard-sign-upsell', didError: !accepted } });
      if (!accepted) {
        // Shopify can partially apply an inventory-limited add. Enable a retry
        // only when we have verified that the child quantities did not change.
        const childQuantity = (all, item) => all.filter((line) => Number(line.variant_id) === item.id && line.parent_relationship?.parent_key === item.parent_line_key).reduce((sum, line) => sum + line.quantity, 0);
        const safeToRetry = items.every((item) => childQuantity(cart.items, item) === childQuantity(updatedCart.items, item));
        errorLabel.textContent = data.description || data.message || this.dataset.error;
        errorLabel.hidden = false;
        addButton.disabled = !safeToRetry;
        return;
      }
      this.#announceAdded();
      this.#clearOffer();
    } catch (error) {
      if (accepted) {
        deferred?.resolve({ detail: {
          sections: addedSections,
          source: 'yard-sign-upsell',
          itemCount: cartBefore.item_count + this.#offers.reduce((sum, offer) => sum + offer.quantity, 0),
          didError: false,
        } });
        this.#announceAdded();
        this.#clearOffer();
      } else {
        deferred?.reject(error);
        errorLabel.textContent = this.dataset.checkCart;
        errorLabel.hidden = false;
        // A failed POST response is ambiguous: the add may have reached Shopify.
        // Keep View cart available instead of risking a duplicate retry.
        addButton.disabled = mutationStarted || error.message === 'cart_changed';
      }
    } finally {
      this.#busy = false;
      declineButton.disabled = false;
      addButton.removeAttribute('aria-busy');
      const pending = this.#pendingOffers;
      this.#pendingOffers = undefined;
      if (pending?.length) await this.#present(pending);
    }
  };

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart_fetch_failed');
    return response.json();
  }

  #announceAdded() {
    let status = this.refs.status;
    if (this.dataset.presentation === 'drawer') {
      const panel = document.querySelector('theme-drawer#cart-drawer dialog');
      if (panel) {
        status = panel.querySelector('[data-yard-sign-upsell-status]');
        if (!status) {
          status = document.createElement('span');
          status.className = 'visually-hidden';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          status.setAttribute('data-yard-sign-upsell-status', '');
          panel.append(status);
        }
      }
    }
    status.textContent = '';
    queueMicrotask(() => { status.textContent = this.dataset.added; });
  }

  dismiss() {
    if (this.#busy) return;
    this.#generation++;
    this.#clearOffer();
  }

  #clearOffer() {
    const focusWasInside = this.#card?.contains(document.activeElement);
    const drawer = this.#row?.closest('theme-drawer');
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#row?.remove();
    this.#row = undefined;
    this.#card?.remove();
    this.#offers = [];
    if (this.refs.popup?.open) {
      this.refs.popup.close();
      unlockScroll(this.refs.popup);
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
    } else if ((focusWasInside || this.#busy) && drawer?.isConnected) {
      drawer.querySelector('button')?.focus();
    }
  }
}

if (!customElements.get('yard-sign-upsell')) customElements.define('yard-sign-upsell', YardSignUpsell);
